<script lang="ts">
  import { modalStore, orderStore } from "../../services/order-options-service";
  import type { OptionsObject, OrderObject } from "../../models/order.interface";
  import BboxOptions from "./BboxOptions.svelte";
  import StartDateOptions from "./StartDateOptions.svelte";
  import EndDateOptions from "./EndDateOptions.svelte";
  import StationsOptions from "./StationsOptions.svelte";
  import IncludeDocumentationOptions from "./IncludeDocumentationOptions.svelte";
  import IncludeAttributesOptions from "./IncludeAttributesOptions.svelte";
  import DataTypeOptions from "./DataTypeOptions.svelte";
  import { fileStore } from "../../services/select-files";
  import { cart } from "../../services/cart-service";
  import IncludeStationLocationOptions from "./IncludeStationLocationOptions.svelte";
  import IncludeStationNameOptions from "./IncludeStationNameOptions.svelte";
  import UnitsOptions from "./UnitsOptions.svelte";
  import FilesOptions from "./FilesOptions.svelte";

  import { onMount } from "svelte";
  import TooltipHybridButton from "../search/TooltipHybridButton.svelte";
  import PageOptions from "./PageOptions.svelte";

  export let selectedFileList: any[];
  export let datasetMetadata: any;

  onMount(() => {
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  });

  function closeOnEscape(e: KeyboardEvent) {
    if (e.key === "Escape") {
      modalStore.closeModal();
    }
  }

  function getOptionType(id: string): string {
    switch (id) {
      case "bbox":
        return "bbox";
      case "startdate":
      case "enddate":
        return "date";
      default:
        return "term";
    }
  }

  async function handleAddToCart() {
    // Process order options from the store values
    const orderOptions: OptionsObject[] = Object.entries($orderStore.values)
      .map(([key, values]) => {
        // Skip empty values arrays
        if (!values || values.length === 0) return null;

        // Find the field definition for this option
        const option = $orderStore.options[key];
        if (!option) return null;

        // Filter to get only selected items
        const selectedItems = values.filter((val) => val.selected);

        // Skip if no values are selected
        if (selectedItems.length === 0) return null;

        // Extract values based on option type
        let selectedValues;
        if (key === "stations" || key == "datatypes") {
          selectedValues = selectedItems.map((val) => ({
            id: val.id,
            value: val.value
          }));
        } else {
          // For other options, just send the values as before
          selectedValues = selectedItems.map((val) => val.value);
        }

        return {
          id: key,
          type: getOptionType(key),
          name: key,
          values: selectedValues
        };
      })
      .filter(Boolean) as OptionsObject[];

    // Create file objects
    const fileObjects = $fileStore.selectedFiles.map((file) => ({
      archiveName: null,
      name: file.name,
      recId: file.id,
      tar: file.tar
    }));


    const sortedOptions = cart.sortOrderOptions(orderOptions)
    
    // Create order object
    const orderObject: OrderObject = {
      orderId: null,
      email: null,
      datasetId: datasetMetadata.id,
      datasetName: datasetMetadata.name,
      format: $orderStore.selectedFormat,
      orderOptions: sortedOptions,
      files: fileObjects,
      submitDate: null,
      orderStatus: null,
      orderType: datasetMetadata.ncei.access.order.type
    };

    cart.addItem(orderObject);
    modalStore.closeModal();
    clearSelections();
  }

  const summaryComponentMap = {
    bbox: BboxOptions,
    startdate: StartDateOptions,
    enddate: EndDateOptions,
    stations: StationsOptions
  };

  const orderOptionsComponentMap = {
    includedocumentation: IncludeDocumentationOptions,
    pages: PageOptions,
    includeattributes: IncludeAttributesOptions,
    includestationlocation: IncludeStationLocationOptions,
    includestationname: IncludeStationNameOptions,
    units: UnitsOptions,
    datatypes: DataTypeOptions,
    files: FilesOptions
  };

  function clearSelections() {
    const filesToRemove = [...$fileStore.selectedFiles];
    filesToRemove.forEach((file) => {
      fileStore.remove(file);
    });
  }
</script>

{#if $modalStore.isModalOpen}
  <div class="modal show d-block" id="orderOptionsModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg">
      <div class="modal-content p-0">
        <div class="modal-header">
          <h1 class="modal-title fs-5 fw-normal">
            Choose Order Options for {datasetMetadata.name}
          </h1>
          <button type="button" class="close" on:click={modalStore.closeModal}>×</button>
        </div>

        <div class="modal-body px-4 py-0">
          <section>
            <div class="section-title my-3">
              SELECTION SUMMARY
              <TooltipHybridButton
                searchUrlQueryParam="selection-summary"
                tooltip="These fields summarize the selections made so far. To edit these fields close this
                modal and modify the search options or the selected files."
              />
              <!--              <i class="bi bi-question-circle-fill ms-2" id="selection-summary-info" />-->
              <!--              <Tooltip target="selection-summary-info" placement="right">-->
              <!--                These fields summarize the selections made so far. To edit these fields close this-->
              <!--                modal and modify the search options or the selected files.-->
              <!--              </Tooltip>-->
            </div>

            {#each Object.entries(summaryComponentMap) as [id, component]}
              {#if id in $orderStore.options}
                <svelte:component this={component} option={$orderStore.options[id]} />
              {/if}
            {/each}
          </section>

          <section>
            <div class="section-title my-3">
              ORDER OPTIONS
              <TooltipHybridButton
                searchUrlQueryParam="order-options"
                tooltip="These options can be modified to customize the order. To edit these fields use the
                buttons below."
              />
              <!--              <i class="bi bi-question-circle-fill ms-2" id="order-options-info" />-->
              <!--              <Tooltip target="order-options-info" placement="right">-->
              <!--                These options can be modified to customize the order. To edit these fields use the-->
              <!--                buttons below.-->
              <!--              </Tooltip>-->
            </div>

            {#each Object.entries($orderStore.options) as [id, option]}
              {#if id in orderOptionsComponentMap}
                <svelte:component this={orderOptionsComponentMap[id]} {option} />
              {/if}
            {/each}
          </section>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn ae-tertiary" on:click={modalStore.closeModal}>
            ✕ Cancel
          </button>
          <button type="button" class="btn ae-primary" on:click={handleAddToCart}>
            + Add Order To Cart
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="modal-backdrop show" />
{/if}

<style>
    /* Modal styling */
    .modal-content {
        border-radius: 8px;
    }

    .modal-header {
        padding: 1.25rem 1.5rem;
        border-bottom: 1px solid #dee2e6;
    }

    .close {
        border: none;
        background: none;
        font-size: 1.5rem;
        padding: 0;
        line-height: 1;
    }

    /* Section titles */
    .section-title {
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-size: 15px;
    }

    /* Footer styling */
    .modal-footer {
        padding: 1rem 1.5rem;
        border-top: 1px solid #dee2e6;
    }
</style>